import { apiRequest } from "./apiClient";

export type AccountSummary = {
  id: number;
  accountNo: string;
  customerId: number | null;
  customerName?: string | null;
  walletBalance: number | null;
  statusId: number | null;
  status?: string | null;
  statusName?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  referenceNo?: string | null;
  cardNumber?: string | null;
  accountType?: string | null;
  address?: string | null;
};

export type PaginationMeta = {
  page: number;
  pageSize: number;
  total: number;
  pages: number;
};

export type PaginatedResponse<TItem> = {
  items: TItem[];
  meta: PaginationMeta;
};

export type AddressDto = {
  id: number;
  addressTypeId: number;
  flatNo?: string | null;
  houseNo?: string | null;
  roadNo?: string | null;
  villageName?: string | null;
  postCode?: string | null;
  districtId: number | null;
  upazilaId: number | null;
};

export type AccountDetailDto = {
  account: {
    id: number;
    accountNo: string;
    walletBalance: number | null;
    statusId: number | null;
  };
  customer: {
    id: number;
    customerName: string;
    mobileNo?: string | null;
    identityTypeId?: number | null;
    identityNo?: string | null;
    dateOfBirth?: string | null;
    genderId?: number | null;
    fatherName?: string | null;
    motherName?: string | null;
    occupationId?: number | null;
    pumpId?: number | null;
    statusId: number | null;
  };
  addresses: AddressDto[];
  lookups?: {
    statuses?: LookupOptionDto[];
    identityTypes?: LookupOptionDto[];
    genders?: LookupOptionDto[];
    occupations?: LookupOptionDto[];
    pumps?: LookupOptionDto[];
    districts?: LookupOptionDto[];
    upazilas?: UpazilaOptionDto[];
  };
};

export type LookupOptionDto = {
  id: number;
  name: string;
};

export type UpazilaOptionDto = LookupOptionDto & {
  districtId: number;
};


export type AccountValidationResponse = {
  accountId: number;
  accountNo: string;
  customerName?: string | null;
  mobileNo?: string | null;
  statusId?: number | null;
  statusName?: string | null;
  walletBalance?: number | null;
};
export type AccountLookupAccount = {
  id: number;
  accountNo: string;
  walletBalance?: number | null;
  statusId?: number | null;
};

export type AccountLookupCustomer = {
  id: number;
  customerName: string;
  mobileNo?: string | null;
};

export type AccountLookupCard = {
  id: number;
  cardNumber: string;
  referenceId?: string | null;
};

export type AccountLookupResponse = {
  account?: AccountLookupAccount | null;
  customer?: AccountLookupCustomer | null;
  addresses?: AddressDto[];
  cards?: AccountLookupCard[] | null;
};
export type AccountSavePayload = {
  account: {
    accountNo?: string;
    walletBalance: number;
    statusId: number;
  };
  customer: {
    customerName: string;
    mobileNo?: string | null;
    identityTypeId?: number | null;
    identityNo?: string | null;
    dateOfBirth?: string | null;
    genderId?: number | null;
    fatherName?: string | null;
    motherName?: string | null;
    occupationId?: number | null;
    pumpId?: number | null;
    statusId: number;
  };
  addresses: Array<{
    addressTypeId: number;
    flatNo?: string | null;
    houseNo?: string | null;
    roadNo?: string | null;
    villageName?: string | null;
    postCode?: string | null;
    districtId?: number | null;
    upazilaId?: number | null;
  }>;
};

export type FetchAccountsOptions = {
  page?: number;
  pageSize?: number;
  search?: string;
  fromDate?: string;
  toDate?: string;
  signal?: AbortSignal;
};

const buildAccountsQuery = (options?: FetchAccountsOptions) => {
  if (!options) {
    return "";
  }

  const params = new URLSearchParams();
  if (options.page && options.page > 0) {
    params.append("page", String(options.page));
  }
  if (options.pageSize && options.pageSize > 0) {
    params.append("pageSize", String(options.pageSize));
  }
  if (options.search?.trim()) {
    params.append("search", options.search.trim());
  }
  if (options.fromDate) {
    params.append("fromDate", options.fromDate);
  }
  if (options.toDate) {
    params.append("toDate", options.toDate);
  }
  const query = params.toString();
  return query ? `?${query}` : "";
};

export async function fetchAccounts(options?: FetchAccountsOptions) {
  const query = buildAccountsQuery(options);
  return apiRequest<PaginatedResponse<AccountSummary>>(`/api/accounts${query}`, {
    signal: options?.signal,
  });
}

type AccountDetailOptions = {
  includeLookups?: boolean;
};

export async function fetchAccountDetail(
  accountId: string,
  signal?: AbortSignal,
  options?: AccountDetailOptions,
) {
  const params = new URLSearchParams();
  if (options?.includeLookups) {
    params.append("includeLookups", "true");
  }
  const query = params.toString();
  const suffix = query ? `?${query}` : "";
  return apiRequest<AccountDetailDto>(`/api/accounts/${accountId}${suffix}`, { signal });
}

export type AccountLookupsDto = AccountDetailDto["lookups"];

export async function fetchAccountLookups(signal?: AbortSignal) {
  return apiRequest<AccountLookupsDto>("/api/accounts/lookups", { signal });
}

export async function createAccount(payload: AccountSavePayload) {
  return apiRequest<AccountDetailDto>(`/api/accounts`, {
    method: "POST",
    body: payload,
  });
}

export async function updateAccount(accountId: string, payload: AccountSavePayload) {
  return apiRequest<void>(`/api/accounts/${accountId}`, {
    method: "PUT",
    body: payload,
  });
}

export async function deleteAccount(accountId: string) {
  return apiRequest<void>(`/api/accounts/${accountId}`, {
    method: "DELETE",
  });
}


export async function validateAccountByNumber(accountNo: string, signal?: AbortSignal) {
  const trimmed = accountNo.trim();
  if (!trimmed) {
    return Promise.reject(new Error("accountNo is required"));
  }
  const params = new URLSearchParams();
  params.append("accountNo", trimmed);
  return apiRequest<AccountValidationResponse>(`/api/accounts/validate?${params.toString()}`, {
    signal,
  });
}

type AccountLookupParams = {
  accountNo?: string;
  mobileNo?: string;
  cardNumber?: string;
  referenceNo?: string;
  signal?: AbortSignal;
};

export function lookupAccount({
  accountNo,
  mobileNo,
  cardNumber,
  referenceNo,
  signal,
}: AccountLookupParams) {
  const params = new URLSearchParams();
  if (accountNo?.trim()) {
    params.append("accountNo", accountNo.trim());
  }
  if (mobileNo?.trim()) {
    params.append("mobileNo", mobileNo.trim());
  }
  if (cardNumber?.trim()) {
    params.append("cardNumber", cardNumber.trim());
  }
  if (referenceNo?.trim()) {
    params.append("referenceNo", referenceNo.trim());
  }
  const query = params.toString();
  if (!query) {
    return Promise.reject(
      new Error("accountNo, mobileNo, cardNumber, or referenceNo is required"),
    );
  }
  return apiRequest<AccountLookupResponse>(`/api/accounts/lookup?${query}`, { signal });
}
